import { useEffect, useRef, useState } from "react";
import {
  generateEmbeddingsJSON,
  getEmbeddingBackendInfo,
  type EmbeddingBackendInfo,
  type MarkerEmbeddingsFile,
} from "@ndp61/cms-shared";

interface EmbeddingComputeProps {
  photos: Blob[];
  onComplete: (result: { embeddings: MarkerEmbeddingsFile; backendInfo: EmbeddingBackendInfo | null }) => void;
}

export function EmbeddingCompute({ photos, onComplete }: EmbeddingComputeProps) {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<"running" | "done" | "error">("running");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [backendInfo, setBackendInfo] = useState<EmbeddingBackendInfo | null>(null);
  const runRef = useRef(false);

  function run() {
    runRef.current = true;
    setStatus("running");
    setProgress(0);
    setErrorMsg(null);

    const refs = photos.map((blob, i) => ({
      label: `photo-${String(i + 1).padStart(3, "0")}.jpg`,
      blob,
    }));

    generateEmbeddingsJSON(refs, (done, total) => {
      setProgress(total > 0 ? Math.round((done / total) * 100) : 0);
    })
      .then((buffer) => {
        if (!runRef.current) return;
        const json = new TextDecoder().decode(buffer);
        const embeddings = JSON.parse(json) as MarkerEmbeddingsFile;
        const info = getEmbeddingBackendInfo();
        setProgress(100);
        setStatus("done");
        setBackendInfo(info);
        onComplete({ embeddings, backendInfo: info });
      })
      .catch((err: unknown) => {
        if (!runRef.current) return;
        setErrorMsg(
          err instanceof Error ? err.message : "Embedding compute failed",
        );
        setStatus("error");
      });
  }

  useEffect(() => {
    run();
    return () => {
      runRef.current = false;
    };
    // run once on mount; photos identity stable from parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={s.container}>
      <h2 style={s.heading}>Step 3 — Compute Embeddings</h2>
      <p style={s.body}>
        Running MobileNetV2 locally in your browser for {photos.length} photo
        {photos.length !== 1 ? "s" : ""}. This may take a moment.
      </p>

      <div style={s.barTrack} role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
        <div style={{ ...s.barFill, width: `${progress}%` }} />
      </div>
      <span style={s.progressLabel}>{progress}%</span>

      {status === "error" && errorMsg && (
        <div style={s.errorBox}>
          <p style={s.errorText}>{errorMsg}</p>
          <button style={s.retryBtn} onClick={run}>
            Retry
          </button>
        </div>
      )}

      {status === "done" && (
        <div style={s.success}>
          <span>Embeddings computed successfully.</span>
          {backendInfo && (
            <span style={{
              display: "inline-block",
              marginLeft: 8,
              padding: "1px 6px",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 700,
              fontFamily: "monospace",
              background: (backendInfo.maliOverride || backendInfo.iosOverride) ? "#fff3cd" : "#d4edda",
              color: (backendInfo.maliOverride || backendInfo.iosOverride) ? "#856404" : "#155724",
              border: `1px solid ${(backendInfo.maliOverride || backendInfo.iosOverride) ? "#ffc107" : "#28a745"}`,
            }}>
              {backendInfo.backend.toUpperCase()}
              {backendInfo.iosOverride ? " (iOS)" : backendInfo.maliOverride ? " (Mali)" : ""}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Inline styles ─────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  heading: {
    fontSize: 17,
    fontWeight: 700,
    color: "#111",
  },
  body: {
    fontSize: 14,
    color: "#444",
    lineHeight: 1.5,
  },
  barTrack: {
    width: "100%",
    height: 10,
    background: "#e0e0e0",
    borderRadius: 5,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    background: "#c8102e",
    borderRadius: 5,
    transition: "width 0.2s ease",
  },
  progressLabel: {
    fontSize: 13,
    color: "#6b6b6b",
  },
  errorBox: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#fdf0ef",
    padding: "12px",
    borderRadius: 8,
  },
  errorText: {
    fontSize: 13,
    color: "#c0392b",
    margin: 0,
  },
  retryBtn: {
    minHeight: 44,
    padding: "0 20px",
    borderRadius: 8,
    border: "none",
    background: "#c8102e",
    color: "#fff",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  success: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: 6,
    fontSize: 13,
    color: "#2d7a2d",
    background: "#f0faf0",
    padding: "8px 12px",
    borderRadius: 8,
  },
};
